import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { SettingsModal } from '../components/SettingsModal';
import { FORALogomark } from '../components/ui/fora-logomark';
import { PersonaIcon } from '../components/ui/persona-icon';
import { Hospital, LogOut, ArrowRight, Settings as SettingsIcon } from 'lucide-react';

const personas = [
  { 
    id: 'patient', 
    title: 'Patient',
    description: 'Send a message or call the clinic',
    path: '/patient'
  },
  { 
    id: 'receptionist', 
    title: 'Receptionist',
    description: 'Manage queue and patient flow',
    path: '/receptionist'
  },
  { 
    id: 'doctor', 
    title: 'Doctor',
    description: 'Conduct consultations',
    path: '/doctor'
  },
  { 
    id: 'billing', 
    title: 'Billing',
    description: 'Process payments',
    path: '/billing'
  },
  { 
    id: 'owner', 
    title: 'Owner',
    description: 'View clinic performance',
    path: '/owner'
  },
  {
    id: 'display',
    title: 'Waiting Display',
    description: 'Public TV board for the waiting room',
    path: '/display',
    public: true
  }
];

export const PersonaSelectorEnhanced = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [visibleCards, setVisibleCards] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    personas.forEach((_, index) => {
      setTimeout(() => {
        setVisibleCards(prev => [...prev, index]);
      }, 100 + (index * 80));
    });
  }, []);

  const handlePersonaSelect = (persona) => {
    if (persona.public) {
      // Open the public TV board in a new tab so this device stays on the role selector.
      window.open(persona.path, '_blank', 'noopener');
    } else {
      navigate(persona.path);
    }
  };

  return (
    <div className="min-h-screen" data-testid="persona-selector-enhanced">
      <div 
        className="fixed top-0 left-0 right-0 z-50 glass-primary"
        style={{ height: '64px' }}
      >
        <div className="h-full flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <FORALogomark size="sm" />
            <span 
              className="font-['Sora'] text-base font-bold tracking-[0.10em]"
              style={{ color: 'var(--color-text-primary)' }}
            >
              FORA
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSettings(true)}
              className="glass-subtle flex items-center gap-2 px-3.5 py-1.5 rounded-full transition-all duration-180 hover:bg-[rgba(61,90,128,0.06)]"
              data-testid="clinic-chip-btn"
            >
              <Hospital
                size={15}
                strokeWidth={1.75}
                style={{ color: 'var(--color-slate)', opacity: 0.7 }}
              />
              <span
                className="font-['Inter'] text-[14px] font-medium"
                style={{ color: '#44403C' }}
              >
                {settings.clinic_name}
              </span>
              <SettingsIcon
                size={13}
                strokeWidth={1.75}
                style={{ color: '#78716C', opacity: 0.7 }}
              />
            </button>

            <button
              onClick={logout}
              className="glass-subtle w-9 h-9 rounded-full flex items-center justify-center hover-lift interactive transition-all duration-180"
              data-testid="logout-button-top"
            >
              <LogOut
                size={16}
                strokeWidth={1.75}
                style={{ color: 'var(--color-slate)' }}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="container-app" style={{ paddingTop: '108px', maxWidth: '1080px', margin: '0 auto' }}>
        <div className="mb-12 text-center">
          <h1
            className="font-['Sora'] text-[40px] font-bold mb-3"
            style={{
              color: '#1C1917',
              letterSpacing: '-0.025em'
            }}
          >
            Select your role
          </h1>
          <p
            className="font-['Inter'] text-[17px]"
            style={{ color: '#44403C' }}
          >
            Choose how you want to enter the system
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {personas.map((persona, index) => (
            <PersonaCard
              key={persona.id}
              persona={persona}
              visible={visibleCards.includes(index)}
              onClick={() => handlePersonaSelect(persona)}
            />
          ))}
        </div>

        <div className="text-center pb-8">
          <p
            className="font-['Inter'] text-[13px]"
            style={{ color: '#A8A29E' }}
          >
            Switch between roles anytime from the top of any screen · Click the clinic chip above to customise clinic and doctor names
          </p>
        </div>
      </div>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
};

const PersonaCard = ({ persona, visible, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative overflow-hidden
        glass-primary rounded-[var(--radius-lg)]
        p-5 flex flex-col justify-between
        hover-lift interactive
        transition-all duration-240
        ${visible ? 'animate-slide-up opacity-100' : 'opacity-0'}
        ${isHovered ? 'shadow-lg' : ''}
      `}
      style={{
        height: '228px',
        transitionProperty: 'transform, box-shadow, background, border'
      }}
      data-testid={`persona-card-${persona.id}-enhanced`}
    >
      {isHovered && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(212, 120, 74, 0.08) 50%, transparent 100%)',
            animation: 'shimmer 800ms ease-out',
            backgroundSize: '200% 100%'
          }}
        />
      )}

      <div className="flex flex-col gap-4 relative z-10">
        <PersonaIcon persona={persona.id} size="lg" />

        <div className="text-left">
          <h3
            className="font-['Sora'] text-xl font-semibold mb-1"
            style={{ color: '#1C1917' }}
          >
            {persona.title}
          </h3>
          <p
            className="font-['Inter'] text-[14px] leading-relaxed"
            style={{ color: '#78716C' }}
          >
            {persona.description}
          </p>
          {persona.public && (
            <span
              className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full font-['Sora'] text-[11px] font-semibold"
              style={{ background: 'rgba(61, 90, 128, 0.10)', color: 'var(--color-slate)' }}
            >
              Public · Opens in new tab
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between relative z-10">
        <span
          className="font-['Sora'] text-[13px] font-semibold"
          style={{ color: '#B5612E' }}
        >
          {persona.public ? 'Launch' : 'Enter'}
        </span>
        <ArrowRight
          size={16}
          strokeWidth={1.75}
          className={`transition-transform duration-240 ${isHovered ? 'translate-x-1' : ''}`}
          style={{ color: '#B5612E' }}
        />
      </div>
    </button>
  );
};

export default PersonaSelectorEnhanced;
